import type { DachSignatur, WandSignatur } from "./illustrationTokens";

export const KATEGORIEN = [
  "Wohnen (100%)",
  "Mischnutzung (Wohnen u. Gewerbe)",
  "Gewerbe (100%)",
] as const;
export type Kategorie = (typeof KATEGORIEN)[number];

export const WOHN_TYPEN = [
  "Einfamilienhaus",
  "Zweifamilienhaus",
  "Mehrfamilienhaus",
  "Wohnhochhaus",
  "Studentenwohnheim",
  "Boardinghouse",
  "Seniorenheim / Betreutes Wohnen",
  "Obdachlosen- /Asylheim / Flüchtlingsheim",
  "Ferienimmobilie",
] as const;
export type WohnTyp = (typeof WOHN_TYPEN)[number];

export const GEWERBE_TYPEN = [
  "Logistik/Spedition/Lager",
  "Büro-/Praxis/ Verwaltung",
  "Handel",
  "Produktion",
  "Hotel oder Beherbergungsbetrieb",
  "Sonstige",
] as const;
export type GewerbeTyp = (typeof GEWERBE_TYPEN)[number];

/**
 * Bauartklassen und Fertighausgruppen.
 *
 * Die Beschreibungen folgen der VEMA-Tabelle „Bauartklassen und
 * Fertighausgruppen" (vom Auftraggeber am 18.08.2026 als Quelle benannt). Die
 * bisherigen Texte waren fachlich falsch: FHG I und FHG II trugen denselben
 * Text („Fertighaus, massiv mit harter Dachung"), BAK IV und BAK V ebenfalls
 * („weiche Dachung"). Tatsächlich unterscheiden sich FHG I/II/III über die
 * Ummantelung und BAK IV/V über die Wandbauart.
 *
 * `wand`/`dach` steuern die Schnittzeichnung (`bauartSchnitt.tsx`), `kurz` ist die
 * Beschreibung ohne Kürzel (der Picker zeigt das Kürzel separat als Pill). Ein
 * zusammengesetztes `label` gibt es bewusst **nicht** mehr: es hielt denselben
 * Text ein zweites Mal vor, wurde nach dem Umbau nirgends mehr gelesen und wäre
 * bei der nächsten fachlichen Korrektur von `kurz` abgedriftet.
 *
 * Die `value`-Strings bleiben unverändert — sie landen als
 * `g.answers.bauartklasse` in den Formulardaten und stehen so in Bestandsdaten.
 * Reihenfolge ebenfalls unverändert.
 */
export const BAUARTKLASSEN = [
  {
    value: "BAK I",
    kurz: "massive Bauweise mit harter Dachung",
    wand: "massiv",
    dach: "hart",
  },
  {
    value: "BAK II",
    kurz: "Stahl/Glas Bauweise mit harter Dachung",
    wand: "stahlglas",
    dach: "hart",
  },
  {
    value: "FHG I",
    kurz: "Fertighaus, in allen Teilen feuerbeständig — harte Dachung",
    wand: "fertigFeuerbestaendig",
    dach: "hart",
  },
  {
    value: "FHG II",
    kurz: "Fertighaus in Leichtbauweise, feuerhemmend ummantelt — harte Dachung",
    wand: "fertigUmmantelt",
    dach: "hart",
  },
  {
    value: "FHG III",
    kurz: "Fertighaus in Leichtbauweise, ohne Ummantelung — harte Dachung",
    wand: "fertigOffen",
    dach: "hart",
  },
  {
    value: "BAK III",
    kurz: "Holzhaus / Lehmfachwerk mit harter Dachung",
    wand: "holz",
    dach: "hart",
  },
  {
    value: "BAK IV",
    kurz: "Massiv- oder Stahlbauweise mit weicher Dachung",
    wand: "massiv",
    dach: "weich",
  },
  {
    value: "BAK V",
    kurz: "Holz- oder Fachwerkbauweise mit weicher Dachung",
    wand: "holz",
    dach: "weich",
  },
] as const satisfies readonly {
  value: string;
  kurz: string;
  wand: WandSignatur;
  dach: DachSignatur;
}[];

export const DACH_TYPEN = [
  "Satteldach",
  "Pultdach",
  "Flachdach",
  "Staffelgeschoss",
] as const;
export type DachTyp = (typeof DACH_TYPEN)[number];
/** Geneigte Dächer — nur bei ihnen gibt es ein Dachgeschoss, das ausgebaut sein kann. */
export const GENEIGTE_DACHFORMEN: readonly string[] = ["Satteldach", "Pultdach"];

export const EIGENTUEMER_ART = [
  "ETG",
  "WEG",
  "Firma",
  "Einzelperson",
  "Erbengemeinschaft",
  "Zwangsverwaltung",
  "Sondereigentumsverwaltung",
  "Sonstige",
] as const;

/**
 * Untertypen der einen Rolle „Verwaltung" (Entscheidung des Fachbereichs E3,
 * 18.08.2026). Zwangs- und Sondereigentumsverwaltung sind **keine** eigenen
 * Rollen: der tatsächliche Eigentümer bleibt in jedem Fall Eigentümer, die
 * Verwaltung tritt daneben. `EIGENTUEMER_ART` führt beide Begriffe historisch
 * ebenfalls — dort beschreiben sie die Art des Eigentümers, hier die Art der
 * Verwaltung; die Werte sind bewusst gleich geschrieben.
 */
export const VERWALTUNGS_ARTEN = [
  "Hausverwaltung",
  "Zwangsverwaltung",
  "Sondereigentumsverwaltung",
] as const;

/**
 * Stellplatzarten mit stabilem Schlüssel.
 *
 * Die xlsx (Z. 7, Punkt 7) verlangt Lage **und** Anzahl **je Art** — die
 * Detailangaben liegen deshalb unter `stellplatzDetails` als Objekt mit genau
 * diesen Schlüsseln. Die Beschriftungen bleiben byte-identisch zur früheren
 * Tupel-Konstante: sie stehen als Werte in `stellplatzArten` in Bestandsdaten
 * und werden in der Vertragsmaske (`AllgemeinBlocks.tsx`) verglichen.
 */
export const STELLPLATZ_ARTEN_DEF = [
  { key: "tiefgarage", label: "Tiefgarage" },
  { key: "garage", label: "Garage" },
  { key: "carport", label: "Carport" },
] as const;

export type StellplatzArtKey = (typeof STELLPLATZ_ARTEN_DEF)[number]["key"];

/** Nur die Beschriftungen — aus der Definition abgeleitet, nie doppelt gepflegt. */
export const STELLPLATZ_ARTEN: readonly string[] = STELLPLATZ_ARTEN_DEF.map(
  (a) => a.label
);

export const STELLPLATZ_LAGE = [
  "im Gebäude",
  "außerhalb",
] as const;

/** Freitext „wo genau?" wird nur bei dieser Lage erfragt (Spec: „wenn innerhalb wo?"). */
export const STELLPLATZ_LAGE_INNEN = "im Gebäude";

export const BRANCHEN_KRITISCH = [
  "Restaurant, Kiosk o.ä.",
  "Diskothek, Table Dance, Stundenhotel o.ä.",
  "Tierkörperverwertung / Geflügelmast",
  "Recycling-Betriebe",
  "Sprengstoff-/Feuerwerks-Herstellung oder -Lagerung",
  "Leihhäuser, Pfandhäuser, Pfandkreditbetriebe",
  "Landwirtschaftliche Betriebe",
] as const;

export const HEIZUNGS_ARTEN = [
  "Wärmepumpe",
  "Fußboden-/Wand-/Deckenheizung",
  "Ölheizung",
  "Gasheizung",
  "Fernwärme",
  "Elektroheizung / Infrarotheizung",
  "Pelletheizung",
  "Geothermie",
  "Blockheizkraftwerk (BHKW)",
  "Solar-/Photovoltaikanlage",
  "Kamin / Kachelofen",
  "Klimaanlage",
  "Lüftungsanlage",
  "Balkonkraftwerk",
] as const;

export const UMWELT_ANLAGEN = [
  "Öltanks",
  "Gastanks",
  "Waschplätze",
  "Ölabscheider",
  "Fettabscheider",
  "Sonstige",
] as const;

/*
 * `SICHERHEITS_ANLAGEN` stand bis zum 19.08.2026 hier und führte die fünf
 * Hauptanlagen. Der Auftraggeber hat den Schritt auf 28 Anlagen in drei
 * Abschnitten erweitert, mit Errichter-Regel und Zusatzfeldern je Eintrag —
 * das steht jetzt vollständig in `anlagenKatalog.ts` (Gruppe `haupt` trägt
 * dieselben fünf Schlüssel und Beschriftungen wie zuvor). Eine zweite,
 * verkürzte Liste hier wäre die Stelle gewesen, an der beide auseinanderlaufen.
 */

/**
 * Bewertung von Bauausführung und Ausstattung — Antwort-Schlüssel
 * `bauausfuehrungBewertung` im Schritt „Bauausführung" und, mit denselben drei
 * Werten, `anbautenBauausfuehrung` im Schritt „Anbauten".
 *
 * Die drei `value`-Strings standen bisher zweimal wörtlich in
 * `pages/RisikoImmobilieStepForm.tsx`. Sie liegen so in Bestandsdaten und
 * bleiben unverändert; sie stehen hier nur noch an einer Stelle, damit die
 * beiden Auswahlen nicht auseinanderdriften.
 *
 * `hinweis` ist der Satz unter dem Kartentitel in der Bildauswahl
 * (`AusstattungsstufePicker`). Die gezeichneten Merkmale selbst (Fenster,
 * Bodenbelag) stehen in `ausstattungBilder.tsx` — sie gehören zur Zeichnung
 * und nicht in den Katalog.
 */
export const AUSSTATTUNGS_STUFEN = [
  {
    value: "gehoben",
    label: "Gehoben",
    hinweis: "Hochwertige Materialien, aufwendige Technik",
  },
  {
    value: "normal",
    label: "Normal",
    hinweis: "Übliche Bauweise und Ausstattung für das Baujahr",
  },
  {
    value: "minder",
    label: "Minder",
    hinweis: "Einfache Ausführung, sparsame Ausstattung",
  },
] as const;

/** Die drei gespeicherten Werte — nicht die Beschriftungen. */
export type AusstattungsStufe = (typeof AUSSTATTUNGS_STUFEN)[number]["value"];

/**
 * Besondere Ausstattungen. `nutzung: "gewerbe"` blendet einen Eintrag beim
 * reinen Wohngebäude aus (Weiche Wohnen ↔ Gewerbe, siehe `nutzungsWeiche.ts`).
 *
 * Seit 10/2026: „Aufzug" ist in Personen- und Lastenaufzug geteilt, die
 * „Kompressoren" stehen nur noch einmal — als Anlage im Schritt Sicherheit
 * („Kompressoren für Lüftung und Druckluft"). Bestandswerte zieht
 * `migriereAusstattung` (altAntworten.ts) um.
 */
export const SONDERAUSSTATTUNGEN = [
  { key: "einbaukuechen", label: "Einbauküchen", extra: ["wert", "einbaudatum"] },
  { key: "moeblierteVermietung", label: "Möblierte Vermietung", extra: ["wert", "was"] },
  { key: "rolltreppen", label: "Rolltreppen", extra: ["wert"], nutzung: "gewerbe" },
  { key: "trafos", label: "Trafos", extra: ["wert"], nutzung: "gewerbe" },
  {
    key: "eLadestationen",
    label: "E-Ladestationen / Wallboxen (innerhalb oder außerhalb)",
    extra: ["wert"],
  },
  { key: "personenaufzug", label: "Personenaufzug", extra: ["wert"] },
  { key: "lastenaufzug", label: "Lastenaufzug", extra: ["wert"], nutzung: "gewerbe" },
  { key: "balkone", label: "Balkone", extra: ["inFlaeche", "anteilProzent"] },
] as const;

export const WELLNESS_EINRICHTUNGEN = [
  { key: "schwimmbad", label: "Schwimmbäder", extra: ["lage", "wert"] },
  { key: "whirlpool", label: "Whirlpool / Dampfbad", extra: ["wert"] },
  { key: "sauna", label: "Sauna", extra: ["wert"] },
] as const;

/**
 * Status je Unterlage im Antwort-Schlüssel `unterlagen`
 * (`{ [itemKey]: value }`). Zwei Verwendungen, beide in `renderUnterlagen`:
 *
 * - Wo NICHT hochgeladen werden kann (Modus `disabled` — beide Kundenwege),
 *   ist das Auswahlfeld mit diesen drei Zuständen die einzige Eingabe des
 *   Schritts; dort wird weiterhin geschrieben.
 * - Wo ein Hochlade-Bereich steht (intern), wird der Wert nur noch GELESEN und
 *   als Zeile „bisher erfasst: …" ausgewiesen.
 *
 * Die `value`s liegen so im Bestand und bleiben unverändert.
 */
export const UNTERLAGEN_STATUS = [
  { value: "nicht_vorhanden", label: "nicht vorhanden" },
  { value: "folgt", label: "folgt" },
  { value: "anbei", label: "anbei (zum Hochladen)" },
] as const;

/**
 * Katalog der Dokumente im Schritt „Unterlagen". Das Gutachten stand bis 10/2026
 * auch hier — es wird aber schon im eigenen Schritt „Gutachten" hochgeladen
 * und war damit doppelt gefragt.
 */
export const UNTERLAGEN_ITEMS = [
  { key: "nebenkostenabrechnung", label: "Nebenkostenabrechnung" },
  { key: "flaechenberechnungen", label: "Flächenberechnungen" },
  { key: "vers_gebaeude", label: "Bestehende Gebäudeversicherung" },
  { key: "vers_haftpflicht", label: "Haus- und Grundbesitzerhaftpflicht" },
  { key: "vers_gewaesser", label: "Gewässerschadenhaftpflicht" },
  { key: "vers_pv", label: "Photovoltaikversicherung" },
  { key: "vers_glas", label: "Bestehende Glasversicherung" },
  { key: "vers_mietverlust", label: "Bestehende Mietverlustversicherung" },
  { key: "rechnung_pv", label: "Rechnung Photovoltaikanlage" },
  { key: "atteste", label: "Atteste (diverse)" },
] as const;

export const STELLUNG_OPTIONS = ["freistehend", "angebaut", "sonstiges"] as const;
export const EIGENNUTZUNG_OPTIONS = ["ja", "teilweise", "nein"] as const;
export const SANIERUNG_UMFANG = ["voll", "teilweise"] as const;
export const SCHADENSARTEN = [
  "Leitungswasser",
  "Brand",
  "Elementar",
  "Sturm/Hagel",
  "Glasbruch",
  "Sonstige",
] as const;

export const getTypenForKategorie = (
  k: string | undefined
): readonly string[] => {
  if (k === "Wohnen (100%)") return WOHN_TYPEN;
  if (k === "Gewerbe (100%)") return GEWERBE_TYPEN;
  return [];
};
